import DealIncomeLink from '../../shared/components/DealIncomeLink';
import EntityLinkButton from '../../../../shared/components/linked-entities/EntityLinkButton';
import React from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from '../../../../shared/assets/AppIcons';
import { AssetIcon } from '../../../../shared/assets';
import {
  formatCompletedDealDate,
  getCompletedStatusLabel,
} from '@/modules/deals/utils';
import { resolveDealResponsible } from '../../shared/utils/dealStaff';

function formatSettlementAmount(amount) {
  if (amount === null || amount === undefined) {
    return null;
  }

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function getClientResolutionHint(status) {
  if (status === 'legacy_fallback') {
    return 'Cliente da snapshot legacy';
  }

  if (status === 'missing_client_id') {
    return 'ClientId mancante';
  }

  if (status === 'unresolved_client_reference') {
    return 'ClientId non risolto nel catalogo clienti';
  }

  return '';
}

const CompletedDealCard = ({
  deal,
  onClick,
  onOpenClient,
  onOpenStaff,
  variant = 'settled',
  provided = null,
  snapshot = null,
  staff = [],
}) => {
  const clientName = deal.clientResolvedName || deal.client || 'Cliente non risolto';
  const clientResolutionHint = getClientResolutionHint(deal.clientResolutionStatus);
  const canOpenClient = Boolean(deal.clientId);
  const responsible = resolveDealResponsible(deal, staff);

  return (
    <article
      ref={provided?.innerRef}
      {...(provided?.draggableProps || {})}
      className={snapshot?.isDragging ? 'deals-card is-dragging' : 'deals-card'}
      onClick={() => onClick(deal)}
    >
      <div className="deals-card__inner">
        <div className="deals-card__header">
          {provided ? (
            <div
              {...provided.dragHandleProps}
              className="deals-card__handle"
              aria-label="Sposta affare completato"
            >
              <AssetIcon name="actions.menu" size={16} />
            </div>
          ) : null}
          <h4 className="deals-card__title">
            {deal.name}
          </h4>

          <CheckCircle2 size={16} className="deals-card__status-icon" aria-hidden="true" />
        </div>

        <div className="deals-card__content">
          <EntityLinkButton
            className="deals-card__section"
            disabled={!canOpenClient}
            aria-label={`Apri cliente ${clientName}`}
            onClick={(e) => {
              if (!canOpenClient) return;
              e.stopPropagation();
              onOpenClient?.(deal.clientId, clientName);
            }}
          >
            <span className="deals-card__label">
              <AssetIcon name="nav.clients" size={16} />
              Cliente:
            </span>
            <p className={canOpenClient ? 'deals-card__value deals-card__link-text' : 'deals-card__value'}>
              {clientName}
            </p>
            {clientResolutionHint ? (
              <p className="deals-card__hint">{clientResolutionHint}</p>
            ) : null}
          </EntityLinkButton>

<DealIncomeLink deal={deal} completed />

          <div>
            <span className="deals-card__label">Completato:</span>
            <p className="deals-card__value">
              {formatCompletedDealDate(deal.completedDate)}
            </p>
          </div>

          {deal.settlementView?.state === 'resolved' ? (
            <div>
              <span className="deals-card__label">Settlement:</span>
              <p className="deals-card__value">
                Incassato {formatSettlementAmount(deal.settlementView.receivedAmount) || '-'} /{' '}
                {formatSettlementAmount(deal.settlementView.totalAmount) || '-'}
              </p>
            </div>
          ) : null}

          {deal.settlementView?.state === 'loading' ? (
            <div className="deals-card__inline-status">
              <Loader2 size={16} className="animate-spin" />
              <p>Verifica settlement...</p>
            </div>
          ) : null}

          {deal.settlementView?.state === 'missing' ? (
            <div role="alert" className="deals-card__inline-status wt-inline-error">
              <AlertCircle size={16} />
              <p>Nessun pagamento collegato</p>
            </div>
          ) : null}

          {deal.settlementView?.state === 'error' ? (
            <div role="alert" className="deals-card__inline-status deals-card__inline-status--error wt-inline-error">
              <AlertCircle size={16} />
              <p>Settlement non disponibile</p>
            </div>
          ) : null}

          {variant === 'settled' && (
            <div>
              <span className="deals-card__label">
                <AssetIcon name="nav.team" size={16} />
                Responsabile:
              </span>
              {responsible.hasLinkedStaff ? (
                <EntityLinkButton
                  type="button"
                  className="deals-card__person-link"
                  aria-label={`Apri scheda staff di ${responsible.label}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenStaff?.(responsible.staffId, responsible.staffMember);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    onOpenStaff?.(responsible.staffId, responsible.staffMember);
                  }}
                >
                  {responsible.label}
                </EntityLinkButton>
              ) : (
                <p className="deals-card__value">
                  {responsible.label}
                </p>
              )}
              {responsible.hasLegacyAssignee ? (
                <p className="deals-card__hint">
                  Label legacy: collegamento staff non disponibile
                </p>
              ) : null}
              {responsible.hasMissingStaffReference ? (
                <p className="deals-card__hint">
                  Dati staff non disponibili
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="deals-card__footer">
          <span className={`deals-badge deals-badge--${variant}`}>
            {getCompletedStatusLabel(variant)}
          </span>
        </div>
      </div>
    </article>
  );
};

export default CompletedDealCard;
